/**
 * DSCheckbox — Design System Checkbox
 *
 * Matches Figma: Medarch-DS v.0.1 / Atoms / Checkbox
 * States: Default, Hover, Selected, Unselect All, Disabled
 */

import type {} from "react";
import {
  Checkbox,
  CheckboxProps,
  FormControlLabel,
  Typography,
  SxProps,
  Theme,
} from "@mui/material";
import { COMPONENT, PRIMITIVE, px } from "../../tokens/design-tokens";

const cb = COMPONENT.checkbox;

interface DSCheckboxProps extends Omit<CheckboxProps, "size"> {
  label?: string;
  labelSx?: SxProps<Theme>;
}

export function DSCheckbox({ label, labelSx, sx, ...rest }: DSCheckboxProps) {
  const checkboxEl = (
    <Checkbox
      size="small"
      {...rest}
      sx={{
        padding: px(PRIMITIVE.spacing[2]),
        borderRadius: px(cb.radius),
        color: PRIMITIVE.color.neutrals[1200],
        "&.Mui-checked": {
          color: PRIMITIVE.color.brandBlue[700],
        },
        "&:hover": {
          backgroundColor: cb.iconBg.hover + "20",
        },
        "&.Mui-disabled": {
          color: PRIMITIVE.color.neutrals[1800],
        },
        "& .MuiSvgIcon-root": {
          fontSize: "18px",
        },
        ...sx,
      }}
    />
  );

  if (!label) return checkboxEl;

  return (
    <FormControlLabel
      control={checkboxEl}
      label={
        <Typography
          sx={{
            fontFamily: PRIMITIVE.typography.fontFamily,
            fontSize: px(PRIMITIVE.typography.fontSize.body.b5),
            fontWeight: PRIMITIVE.typography.fontWeight.regular,
            lineHeight: px(PRIMITIVE.typography.lineHeight["150%"][12]),
            color: rest.disabled ? cb.text.disabled : cb.text.default,
            ...labelSx,
          }}
        >
          {label}
        </Typography>
      }
      sx={{ marginLeft: 0, gap: px(PRIMITIVE.spacing[4]) }}
    />
  );
}
